import { ColumnDef } from "@tanstack/react-table";
import { Mentorship } from "@/lib/api/dashboard";
import { DataTableColumnHeader } from "@/components/data-table/data-table-column-header";
import { StarIcon } from "@heroicons/react/24/outline";
import { ClockIcon, MessageCircleIcon } from "lucide-react";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { Badge } from "@/components/ui/badge";
import { getDaysAgo } from "@/lib/utils";
import {
  getMenteeDisplayName,
  getMenteeDisplayPhoto,
  inferRedactedFieldsFromNulls,
} from "@/lib/redacted-display";

// Column definitions for the mentorships table
export const mentorshipColumns: ColumnDef<Mentorship>[] = [
  {
    accessorKey: "mentee_name",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-left"
        column={column}
        title="Teen"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const mentee = {
        id: row.original.mentee_id,
        name: row.original.mentee_name,
        profile_picture_url: row.original.mentee_profile_picture_url,
        redacted_fields: inferRedactedFieldsFromNulls({
          name: row.original.mentee_name,
          profile_picture_url: row.original.mentee_profile_picture_url,
        }),
      };
      const displayPhotoRaw = getMenteeDisplayPhoto(mentee);
      const { url: profileUrl } = useSignedMedia(displayPhotoRaw ?? "");
      const displayName = getMenteeDisplayName(mentee);

      return (
        <div className="flex items-center gap-3">
          {profileUrl && displayPhotoRaw ? (
            <img
              src={profileUrl}
              alt={displayName}
              className="h-10 w-10 rounded-full"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-gray-200" />
          )}
          <div>
            <div className="font-medium text-gray-900">{displayName}</div>
            <div className="text-sm text-gray-500">
              Last message {getDaysAgo(row.original.last_message_date)}{" "}
              days ago
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "teen_days_engaged",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-left"
        column={column}
        title="Engagement"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const engagedDays =
        parseInt(row.original.unique_days_engaged_this_month) || 0;

      return (
        <div className="flex gap-1">
          {[...Array(5)].map((_, i) => {
            let circleColor;
            if (engagedDays >= 5) {
              // 5 or more days: all green
              circleColor = "bg-green-500";
            } else if (i < engagedDays) {
              // Less than 5 days: show engaged days in red
              circleColor = "bg-red-500";
            } else {
              // Remaining circles are grey
              circleColor = "bg-gray-200";
            }

            return (
              <div key={i} className={`h-3 w-3 rounded-full ${circleColor}`} />
            );
          })}
        </div>
      );
    },
  },
  {
    accessorKey: "average_response_time",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-left"
        column={column}
        title="Response time"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="text-gray-500">
        {row.original.average_response_time
          ? `${parseFloat(row.original.average_response_time).toFixed(0)} min`
          : "-"}
      </div>
    ),
  },
  {
    accessorKey: "average_rating",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-left"
        column={column}
        title="Rating"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        <StarIcon className="h-4 w-4 text-gray-500" />
        <span className="text-gray-500">
          {parseFloat(row.original.average_rating || "0").toFixed(1)}
        </span>
      </div>
    ),
  },
  {
    accessorKey: "teen_messages",
    id: "messages",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-left"
        column={column}
        title="Messages"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        <MessageCircleIcon className="h-4 w-4 text-gray-500" />
        <span className="text-gray-500">{row.original.teen_messages}</span>
      </div>
    ),
  },
  {
    accessorKey: "days_to_churn",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-left"
        column={column}
        title="Days to churn"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="flex items-center gap-1">
        <ClockIcon className="h-4 w-4 text-gray-500" />
        <span className="text-gray-500">{row.original.days_to_churn}</span>
      </div>
    ),
  },
  {
    accessorKey: "days_in_mentorship",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-left"
        column={column}
        title="Mentorship"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => (
      <div className="text-gray-500">
        {row.original.days_in_mentorship} days
      </div>
    ),
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <DataTableColumnHeader
        className="text-left"
        column={column}
        title="Status"
      />
    ),
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const status = row.original.deactivated ? "Deactivated" : "Active";

      return (
        <Badge
          className={
            row.original.deactivated
              ? "border-red-600 bg-red-50 text-red-900 hover:bg-red-100"
              : "border-green-600 bg-green-50 text-green-900 hover:bg-green-100"
          }
        >
          {status}
        </Badge>
      );
    },
  },
];
